import { useCallback, useEffect, useMemo, useState } from 'react';

import { errorKey, type AuthApi, type GameCard, type PpLiveFeed } from '../auth/api.ts';
import { config } from '../config';
import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { MoonMark } from '../ui/MoonMark';
import { iconUrl } from './gameIcon';
import { SEARCH_FROM, PREVIEW_LIMIT, HOT_FILTER, groupGames, lobbySections, gameName, tagKey, playable } from './gameList';
import { usePpLiveTables } from './ppLive';
import './lobby-tab.css';

/**
 * 大厅。**游戏列表来自后台**（`GET /games`），不再写死在前端。
 *
 * 为什么这算一件正经事：上架、下架、改排序、挂维护 —— 四件都是运营动作。
 * 写死在前端的话，每一件都要走一次「改代码 → 评审 → 构建 → 发版 →
 * 缓存刷新」。真出事的时候（某个游戏结算异常，要立刻摘掉）那条链路太长了。
 *
 * ## 拉不到就说拉不到
 *
 * **不留一份写死的兜底清单。** 兜底的后果是：后台把一个游戏下架了，
 * 而某些拉取失败的玩家那儿它还在 —— 点进去才被后端拦下来，
 * 而那时谁都说不清他看到的是哪一份列表。
 *
 * ## 维护中的游戏列出来，但点不进去
 *
 * 直接让它消失的话，客服第一个电话就是"我的游戏没了"。灰着摆在那儿，
 * 玩家看得见"它还在，只是在修"。**真正的拦截在后端**（account/launch.py）——
 * 这里灰掉只是礼貌。
 */
export function LobbyTab(
  { api, onPlay }: { api: AuthApi; onPlay(code: string): void },
) {
  const { t, locale } = useI18n();
  const [games, setGames] = useState<GameCard[] | null>(null);
  const [liveFeed, setLiveFeed] = useState<PpLiveFeed>();
  const live = usePpLiveTables(liveFeed);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [provider, setProvider] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const providers = useMemo(() => groupGames(games ?? []), [games]);
  // A provider removed from the catalog must not leave an invisible active filter.
  const selected = provider !== null && (provider === HOT_FILTER || providers.some((p) => p.provider === provider)) ? provider : null;
  const groups = useMemo(() => lobbySections(games ?? [], q, selected, expanded, liveFeed?.tables, live),
    [games, q, selected, expanded, liveFeed, live]);
  const shownCount = groups.filter((group) => selected === HOT_FILTER || group.provider !== HOT_FILTER)
    .reduce((n, group) => n + group.count, 0);
  //: 图标加载失败的游戏。它们回到占位
  const [broken, setBroken] = useState<ReadonlySet<string>>(() => new Set());

  const load = useCallback(async () => {
    try {
      const catalog = await api.games();
      setGames(catalog.games);
      setLiveFeed(catalog.liveTables);
      setErr('');
    } catch (e) {
      setErr(t(errorKey(e)));
    }
  }, [api, t]);

  useEffect(() => { void load(); }, [load]);

  const groupName = (key: string) => key === HOT_FILTER ? t('lobby.hot') : key || t('lobby.otherProvider');
  const selectProvider = (key: string | null) => {
    setProvider(key);
    setExpanded(new Set());
  };

  return (
    <section className="stack">
      <div className="luna-welcome">
        <div className="luna-welcome__copy">
          <span className="luna-welcome__domain">laroluna.com</span>
          <h2 className="luna-welcome__title">{t('luna.title')}</h2>
          <p className="luna-welcome__note">{t('luna.note')}</p>
        </div>
        <div className="luna-welcome__art" aria-hidden="true">
          <span className="luna-welcome__orbit" />
          <MoonMark className="luna-welcome__moon" />
          <span className="luna-welcome__star">✦</span>
        </div>
      </div>
      <div className="spread">
        <h1 className="h1">{t('lobby.title')}</h1>
        {/* 复数由 t() 按语言挑 —— 英语一款时是 "1 game" 不是 "1 games"。
            还没拉到之前不显示数量：显示 0 会闪一下"一个游戏都没有"。 */}
        {games && (
          <span className="small dim">{t('lobby.count', { n: shownCount })}</span>
        )}
      </div>

      {err && (
        <div className="card stack">
          <p className="small">{err}</p>
          <Button variant="ghost" onClick={() => void load()}>
            {t('action.retry')}
          </Button>
        </div>
      )}

      {games && games.length === 0 && !err && (
        <p className="small dim">{t('lobby.none')}</p>
      )}

      {games && games.length > 0 && (
        <div className="lobby-providers" role="group" aria-label={t('lobby.providers')}>
          <button type="button" className="lobby-provider" aria-pressed={selected === null}
                  onClick={() => selectProvider(null)}>
            {t('lobby.allProviders')}<span className="lobby-provider__count">{games.length}</span>
          </button>
          <button type="button" className="lobby-provider" aria-pressed={selected === HOT_FILTER}
                  onClick={() => selectProvider(HOT_FILTER)}>
            {t('lobby.hot')}<span className="lobby-provider__count">{Math.min(games.length, PREVIEW_LIMIT)}</span>
          </button>
          {providers.map((p) => (
            <button key={p.provider} type="button" className="lobby-provider"
                    aria-pressed={selected === p.provider} onClick={() => selectProvider(p.provider)}>
              {p.provider || t('lobby.otherProvider')}
              <span className="lobby-provider__count">{p.games.length}</span>
            </button>
          ))}
        </div>
      )}

      {games && games.length >= SEARCH_FROM && (
        <input type="search" className="lobby-search" value={q}
               placeholder={t('lobby.search')} aria-label={t('lobby.search')}
               enterKeyHint="search" autoComplete="off"
               onChange={(e) => { setQ(e.target.value); setExpanded(new Set()); }} />
      )}
      {q.trim() && games && games.length > 0 && shownCount === 0 && (
        <p className="small dim">{t('lobby.noMatch', { q: q.trim() })}</p>
      )}

      {groups.map((group) => (
        <section className="lobby-provider-group stack" key={group.provider}
                 aria-label={groupName(group.provider)}>
          <div className="lobby-provider-group__heading">
            <h2 className="h2">{groupName(group.provider)}</h2>
            <span className="small dim">{t('lobby.count', { n: group.count })}</span>
            {group.provider !== HOT_FILTER && group.totalCards > PREVIEW_LIMIT && (
              <Button className="lobby-more" aria-expanded={expanded.has(group.provider)}
                      aria-controls={`lobby-games-${encodeURIComponent(group.provider)}`}
                      aria-label={`${groupName(group.provider)}: ${t(expanded.has(group.provider) ? 'lobby.less' : 'lobby.more')}`}
                      onClick={() => setExpanded((current) => {
                        const next = new Set(current);
                        if (next.has(group.provider)) next.delete(group.provider);
                        else next.add(group.provider);
                        return next;
                      })}>
                {t(expanded.has(group.provider) ? 'lobby.less' : 'lobby.more')}
              </Button>
            )}
          </div>
          <div className="games" id={`lobby-games-${encodeURIComponent(group.provider)}`}>
            {group.entries.map(({ game: g, table: binding }) => {
              if (binding) {
                const { tableId, gameCode } = binding;
                const table = live[tableId];
                const off = !playable(g) || table?.open === false;
                const money = (n: number) => new Intl.NumberFormat(locale, {
                  style: 'currency', currency: liveFeed!.currency, maximumFractionDigits: 2,
                }).format(n);
                return <button key={`table:${tableId}`} type="button" className={off ? 'game live-table is-off' : 'game live-table'}
                               disabled={off} onClick={() => onPlay(gameCode)}>
                  <span className="game__art" aria-hidden="true">
                    {table?.image && !broken.has(`dga:${tableId}:${table.image}`) &&
                      <img className="game__icon" src={table.image} alt=""
                           loading="lazy" decoding="async" draggable={false}
                           onError={() => setBroken((s) => new Set(s).add(`dga:${tableId}:${table.image}`))} />}
                    {table?.open === false && <span className="game__off">{t('lobby.tableClosed')}</span>}
                  </span>
                  <span className="game__meta">
                    <span className="game__name">{table?.name || gameName(g, locale)}</span>
                    {table?.newTable && <span className="small dim">{t('lobby.newTable')}</span>}
                    <span className="small dim">{table?.dealer ? `${t('lobby.dealer')}: ${table.dealer}` :
                      table ? g.provider : t('lobby.liveUnavailable')}</span>
                    {table?.minBet != null && table.maxBet != null &&
                      <span className="small dim">{t('lobby.betLimits')}: {money(table.minBet)}–{money(table.maxBet)}</span>}
                    {table?.seated != null && <span className="small dim">{t('lobby.players')}: {table.seated}</span>}
                    {table?.availableSeats != null && <span className="small dim">{t('lobby.seats')}: {table.availableSeats}</span>}
                    {table && table.recent.length > 0 &&
                      <span className="small dim">{t('lobby.recentResults')}: {table.recent.join(' · ')}</span>}
                    {table?.shoe && <span className="small dim">{t('lobby.shoe')}: {table.shoe.banker} / {table.shoe.player} / {table.shoe.tie}</span>}
                  </span>
                </button>;
              }
              const tag = tagKey(g);
              const off = !playable(g);
              const offKey = g.status === 'maintenance' ? 'lobby.maintenance' : 'lobby.unavailable';
              const src = broken.has(g.code) ? null : iconUrl(g.icon, config.gameUrl);
              return (
                <button key={`game:${g.code}`} type="button"
                        className={off ? 'game is-off' : 'game'}
                        disabled={off}
                        aria-label={off ? `${gameName(g, locale)} — ${t(offKey)}` : undefined}
                        onClick={() => onPlay(g.code)}>
                  {/* 图标是装饰：名字就在下面，读屏器不用再念一遍 —— 所以 alt 为空 */}
                  <span className={src ? 'game__art' : 'game__art game__art--blank'}
                        aria-hidden="true">
                    {src && (
                      <img className="game__icon" src={src} alt=""
                           loading="lazy" decoding="async" draggable={false}
                           // **404 了就回到占位**，不留一张裂图。浏览器不会重试，
                           // 所以记下来就行，不用考虑"之后又好了"
                           onError={() => setBroken((s) => new Set(s).add(g.code))} />
                    )}
                    {off && <span className="game__off">{t(offKey)}</span>}
                  </span>
                  <span className="game__meta">
                    {/* 游戏名我们不翻（商品名），厂商给了官方译名才换 —— 见 gameList.ts */}
                    <span className="game__name">{gameName(g, locale)}</span>
                    <span className="small dim">
                      {/* 少一段就少一段：厂商游戏没有盘面，别显示 "PG · — · 0 线" */}
                      {tag
                        ? t(tag, { provider: g.provider ?? '—', grid: g.grid ?? '—', n: g.lines ?? 0 })
                        : g.provider ?? ''}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </section>
  );
}
